import type { MtfDetail } from '@/lib/forex-mtf-api';
import { resolveDisplayReason, statusLabel } from './mtf-display';

export type DisplayStage = MtfDetail['stages'][number] & {
  evidence_source?: 'strategy_candidate';
};

export const executionStages = new Set(['master_execution', 'master_fill', 'follower_handoff', 'follower_result_summary']);
const strategyStages = new Set(['strategy_check', 'strategy_candidate', 'mtf_strategy_candidate']);
export const isStrategyStage = (stage: DisplayStage) => strategyStages.has(stage.stage);

export function object(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

export function strategyReason(detail: MtfDetail): string | null {
  const candidate = detail.strategy_candidate;
  const codes = candidate?.evaluation?.reason_codes;
  return typeof candidate?.reason_code === 'string' ? candidate.reason_code
    : Array.isArray(codes) && typeof codes[0] === 'string' ? codes[0] : null;
}

export function candidateDirection(detail: MtfDetail): string | null {
  const candidate = detail.strategy_candidate;
  const directions = candidate?.eligible_directions || candidate?.evaluation?.eligible_directions;
  const value = candidate?.status === 'CANDIDATE'
    ? candidate.candidate_direction || (Array.isArray(directions) ? directions[0] : null)
    : object(candidate?.evaluation?.evidence).direction;
  return value === 'BUY' ? '买入' : value === 'SELL' ? '卖出' : null;
}

export function runDisplayReason(detail: MtfDetail): string {
  const laterEvidence = hasPostStrategyEvidence(detail);
  const explanationReason = detail.explanation?.stage === 'strategy_candidate' && laterEvidence ? null : detail.explanation?.reason_code;
  const code = detail.run.error_code || explanationReason || (!laterEvidence ? strategyReason(detail) : null);
  return resolveDisplayReason(code, detail.explanation);
}

/** Conservative guard: any later evidence prevents us claiming the AI was not called. */
export function hasPostStrategyEvidence(detail: MtfDetail): boolean {
  return Boolean(detail.full || detail.admission || detail.execution_lineage
    || detail.full_attempts?.length || detail.role_results?.length
    || detail.master_judgment && detail.master_judgment.status !== 'not_called'
    || detail.stages.some(stage =>
      ['full_decision', 'admission', 'master_signal', ...executionStages].includes(stage.stage)
      && !['not_started', 'not_applicable'].includes(stage.status)));
}

export function stoppedAtStrategy(detail: MtfDetail): boolean {
  return detail.run.status === 'no_signal'
    && ['HOLD', 'NO_CANDIDATE'].includes(detail.strategy_candidate?.status || '')
    && !hasPostStrategyEvidence(detail);
}

/** Builds UI nodes from existing evidence. Does not mutate the response or write runtime stages. */
export function buildDisplayStages(detail: MtfDetail): DisplayStage[] {
  const stages = detail.stages.map(stage => ({...stage}));
  // Preserve server stages and unknown extensions in their original relative order.
  let strategySeen = false;
  const unique = stages.filter(stage => {
    if (!isStrategyStage(stage)) return true;
    if (strategySeen) return false;
    strategySeen = true;
    return true;
  });
  if (strategySeen) return unique;
  const candidate = detail.strategy_candidate;
  // Empty/legacy details have no evidence about this extra step.
  if (!candidate && !stages.some(stage => stage.stage === 'fast_gate')) return unique;
  const fastBlocked = !candidate && stages.some(stage => stage.stage === 'fast_gate'
    && ['blocked', 'failed', 'no_signal', 'hold'].includes(stage.status));
  const status = candidate?.status === 'CANDIDATE' ? 'strategy_candidate'
    : candidate && ['HOLD', 'NO_CANDIDATE'].includes(candidate.status) ? 'strategy_no_candidate'
    : candidate && ['BLOCKED', 'NOT_READY'].includes(candidate.status) ? 'blocked'
    : fastBlocked ? 'not_started' : 'unknown';
  const node: DisplayStage = {
    stage: 'strategy_check', status, reason_code: strategyReason(detail), details: {},
    ...(candidate ? {evidence_source: 'strategy_candidate' as const} : {}),
  };
  const fastIndex = unique.findIndex(stage => stage.stage === 'fast_gate');
  const nextIndex = unique.findIndex(stage => stage.stage === 'full_decision' || stage.stage === 'admission' || stage.stage === 'master_signal' || executionStages.has(stage.stage));
  unique.splice(fastIndex >= 0 ? fastIndex + 1 : nextIndex >= 0 ? nextIndex : unique.length, 0, node);
  return unique;
}

export function hasExecutionEvidence(detail: MtfDetail) {
  return Boolean(detail.execution_lineage || detail.master_orders && detail.master_orders.status !== 'no_signal'
    || detail.master_orders?.items?.length
    || detail.stages.some(stage => (executionStages.has(stage.stage) || stage.stage === 'master_signal')
      && (!['not_started', 'not_applicable'].includes(stage.status) || Object.keys(stage.details || {}).length > 0)));
}

export function accountResultLabel(account: Record<string, unknown>) {
  const result = object(account.result), plan = object(account.plan);
  if (account.command_status === 'unknown') return '执行结果待对账';
  if (plan.state === 'partially_completed') return '部分步骤已执行，剩余步骤未完成';
  if (result.action === 'OPEN' && result.phase === 'filled' && result.order_effect === 'filled') return '已成交';
  if (result.phase === 'applied' && result.order_effect === 'applied') {
    if (result.action === 'CLOSE') return '已平仓';
    if (result.action === 'MODIFY_PROTECTION') return '保护已更新';
  }
  const labels: Record<string, string> = {queued:'命令排队中', leased:'命令已领取', received:'EA 已接收，尚未确认结果', submit_authorized:'已获提交许可，等待结果', failed:'执行失败', rejected:'执行被拒绝', expired:'命令已过期', completed:'命令已完成，业务结果待核对'};
  if (typeof account.command_status === 'string' && account.command_status) return labels[account.command_status] || `命令状态：${account.command_status}，结果待核对`;
  if (typeof plan.state === 'string' && plan.state) return `执行计划：${statusLabel(plan.state)}`;
  return account.target_state ? `执行目标：${statusLabel(String(account.target_state))}` : '执行状态待核对';
}
